/**
 * Self-Exclusion / Bans (admin)
 *
 * Manage member restrictions: bans and self-exclusions, including "pre-bans"
 * for people who do NOT have an app account yet. A pre-ban is matched to a
 * future signup by name + date of birth or player-club member number and
 * blocks that signup. Restrictions can be permanent or carry an expiry
 * (temporary bans auto-lift). Backed by /admin/restrictions (users:edit).
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../api';
import toast from 'react-hot-toast';
import { Ban, Plus, X, RefreshCw, ShieldOff, Search } from 'lucide-react';

function fmtDate(d) {
  if (!d) return '-';
  try {
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '-';
  }
}

function expiryLabel(r) {
  if (r.liftedAt) return `Lifted ${fmtDate(r.liftedAt)}`;
  if (!r.expiresAt) return 'Permanent';
  return `Until ${fmtDate(r.expiresAt)}`;
}

export default function Restrictions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/restrictions', { params: { all: showAll ? 'true' : 'false' } });
      setItems(res.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to load restrictions');
    } finally {
      setLoading(false);
    }
  }, [showAll]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) =>
      [i.firstName, i.lastName, i.memberNumber, i.accountName, i.accountEmail, i.reason]
        .some((v) => (v || '').toLowerCase().includes(q))
    );
  }, [items, query]);

  const lift = useCallback(async (id) => {
    if (!window.confirm('Lift this restriction? The person will be able to use the app again.')) return;
    try {
      await api.post(`/admin/restrictions/${id}/lift`);
      toast.success('Restriction lifted');
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to lift restriction');
    }
  }, [fetchData]);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
            <Ban className="text-red-400" size={20} />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-white">Self-Exclusion &amp; Bans</h1>
            <p className="text-xs text-gray-400">
              Block members from the app &mdash; including people not yet signed up (matched by name + DOB or member number).
            </p>
          </div>
        </div>
        <button
          onClick={() => setAdding(true)}
          className="flex items-center gap-2 bg-red-500/90 hover:bg-red-500 text-white text-sm font-medium px-4 py-2 rounded-lg"
        >
          <Plus size={16} /> Add restriction
        </button>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-2.5 text-gray-500" size={16} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, member #, email, reason..."
            className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-gray-500"
          />
        </div>
        <label className="flex items-center gap-2 text-xs text-gray-400 select-none">
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
          Show lifted / expired
        </label>
        <button onClick={fetchData} className="text-gray-400 hover:text-white p-2" title="Refresh">
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
        {loading ? (
          <div className="py-12 text-center text-gray-400">
            <RefreshCw className="animate-spin inline mr-2" size={16} /> Loading...
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center text-gray-500">
            <ShieldOff className="inline mb-2" size={22} />
            <div>No {showAll ? '' : 'active '}restrictions.</div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-700">
                <th className="px-4 py-3">Person</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Reason</th>
                <th className="px-4 py-3">Duration</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-b border-gray-800/60 last:border-0">
                  <td className="px-4 py-3">
                    <div className="text-white">
                      {r.accountName || [r.firstName, r.lastName].filter(Boolean).join(' ') || '-'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {r.accountEmail ? `${r.accountEmail} - ` : ''}
                      {r.dateOfBirth ? `DOB ${fmtDate(r.dateOfBirth)}` : ''}
                      {r.memberNumber ? `${r.dateOfBirth ? ' - ' : ''}#${r.memberNumber}` : ''}
                      {r.hasAccount ? '' : ' - (pre-ban, no account yet)'}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs ${r.kind === 'ban' ? 'bg-red-500/15 text-red-400' : 'bg-amber-500/15 text-amber-400'}`}>
                      {r.kind === 'ban' ? 'Ban' : 'Self-exclusion'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-300 max-w-[16rem] truncate">{r.reason || '-'}</td>
                  <td className="px-4 py-3 text-gray-300">{expiryLabel(r)}</td>
                  <td className="px-4 py-3">
                    {r.active ? (
                      <span className="text-red-400 text-xs font-medium">Active</span>
                    ) : (
                      <span className="text-gray-500 text-xs">Inactive</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {r.active && (
                      <button onClick={() => lift(r.id)} className="text-xs text-emerald-400 hover:text-emerald-300">
                        Lift
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {adding && <AddRestrictionModal onClose={() => setAdding(false)} onSaved={() => { setAdding(false); fetchData(); }} />}
    </div>
  );
}

function AddRestrictionModal({ onClose, onSaved }) {
  const [kind, setKind] = useState('self_exclusion');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [memberNumber, setMemberNumber] = useState('');
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState('permanent');
  const [customDate, setCustomDate] = useState('');
  const [saving, setSaving] = useState(false);

  const canSave = (lastName.trim() && dateOfBirth) || memberNumber.trim();

  const save = async () => {
    if (!canSave) {
      toast.error('Enter a last name + date of birth, or a member number.');
      return;
    }
    if (duration === 'custom' && !customDate) {
      toast.error('Pick a custom end date, or choose a preset.');
      return;
    }
    setSaving(true);
    try {
      const body = {
        kind,
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
        dateOfBirth: dateOfBirth || undefined,
        memberNumber: memberNumber.trim() || undefined,
        reason: reason.trim() || undefined,
      };
      if (duration === 'custom') body.expiresAt = new Date(customDate).toISOString();
      else body.durationPreset = duration; // '1y' | '5y' | 'permanent'
      await api.post('/admin/restrictions', body);
      toast.success('Restriction added');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to add restriction');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-800 border border-gray-700 rounded-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-gray-700">
          <h3 className="text-lg font-semibold text-white">Add restriction</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={20} /></button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Type</label>
            <div className="flex gap-2">
              {[
                { v: 'self_exclusion', l: 'Self-exclusion' },
                { v: 'ban', l: 'Ban' },
              ].map((o) => (
                <button
                  key={o.v}
                  onClick={() => setKind(o.v)}
                  className={`flex-1 py-2 rounded-lg text-sm border ${kind === o.v ? 'border-red-500 bg-red-500/10 text-white' : 'border-gray-700 text-gray-400'}`}
                >
                  {o.l}
                </button>
              ))}
            </div>
          </div>

          <p className="text-xs text-gray-500">
            Enter <span className="text-gray-300">last name + date of birth</span> and/or a{' '}
            <span className="text-gray-300">member number</span>. If they already have an account it is
            blocked now; otherwise the block applies when they sign up.
          </p>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">First name</label>
              <input value={firstName} onChange={(e) => setFirstName(e.target.value)} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Last name</label>
              <input value={lastName} onChange={(e) => setLastName(e.target.value)} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Date of birth</label>
              <input type="date" value={dateOfBirth} onChange={(e) => setDateOfBirth(e.target.value)} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Member # (optional)</label>
              <input value={memberNumber} onChange={(e) => setMemberNumber(e.target.value)} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white" />
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Duration</label>
            <div className="grid grid-cols-4 gap-2">
              {[
                { v: '1y', l: '1 year' },
                { v: '5y', l: '5 years' },
                { v: 'permanent', l: 'Permanent' },
                { v: 'custom', l: 'Custom' },
              ].map((o) => (
                <button
                  key={o.v}
                  onClick={() => setDuration(o.v)}
                  className={`py-2 rounded-lg text-xs border ${duration === o.v ? 'border-red-500 bg-red-500/10 text-white' : 'border-gray-700 text-gray-400'}`}
                >
                  {o.l}
                </button>
              ))}
            </div>
            {duration === 'custom' && (
              <input
                type="date"
                value={customDate}
                onChange={(e) => setCustomDate(e.target.value)}
                className="mt-2 w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
              />
            )}
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Reason / notes</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white" />
          </div>
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-gray-700">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-300 hover:text-white">Cancel</button>
          <button onClick={save} disabled={saving || !canSave} className="px-4 py-2 rounded-lg text-sm bg-red-500/90 hover:bg-red-500 text-white disabled:opacity-50">
            {saving ? 'Saving...' : 'Add restriction'}
          </button>
        </div>
      </div>
    </div>
  );
}
